'use client';

import React from 'react';
import { resizedUrl, buildSrcSet } from '@/lib/img';

// Drop-in <img> that serves right-sized variants via Cloudflare Image Resizing
// (when enabled) and always lazy-loads + async-decodes. Passthrough-safe: when
// resizing is off it renders a plain lazy <img> with the original src.
//
// Props mirror <img>, plus:
//   width    - the intrinsic render width hint (px) for the default variant
//   widths   - the srcSet ladder (defaults cover phone → desktop)
//   sizes    - the CSS `sizes` attribute (defaults 100vw)
//   quality  - 1..100 (default 80)
//   fit      - Cloudflare fit mode (default 'cover')

const DEFAULT_WIDTHS = [320, 480, 640, 828, 1080, 1280];

const SmartImage = React.forwardRef(function SmartImage({
  src,
  width = 1080,
  widths = DEFAULT_WIDTHS,
  sizes = '100vw',
  quality = 80,
  fit = 'cover',
  alt = '',
  loading = 'lazy',
  ...props
}, ref) {
  const primary = src ? resizedUrl(src, { width, quality, fit }) : src;
  const srcSet = buildSrcSet(src, widths, quality);
  return (
    <img
      ref={ref}
      src={primary}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      loading={loading}
      decoding="async"
      {...props}
    />
  );
});

export default SmartImage;
